'use client';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, DraftingCompass, XCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { LoadingSpinner, PhotoFrameCorners } from '@/shared/ui';
import {
  getProjectAngleValidationStatus,
  isProjectAngleValidating,
  type ProjectAngle,
} from '@/types/project-angle';
import { BriefTile } from './BriefTile';

interface ProjectAngleTileProps {
  angle: ProjectAngle;
  index: number;
  onEdit: () => void;
  isDeleting?: boolean;
}

/**
 * Square grid tile for a source angle ("View") in the unified Brief materials
 * grid. Clean image only — no name, no "not used" badge, no main marker (the
 * main angle is chosen at concept creation). Tapping the tile opens the angle
 * editor; the whole tile is draggable for reorder (the click-vs-drag
 * disambiguation is handled by the page-level PointerSensor activation
 * distance). Renders through the shared BriefTile so chrome, sizing, and the
 * "View" caption stay identical to Docs and Sketches. Mirrors Apple/Android.
 */
export function ProjectAngleTile({
  angle,
  index,
  onEdit,
  isDeleting = false,
}: ProjectAngleTileProps) {
  const t = useTranslations('angles');
  const tCaption = useTranslations('briefSketches');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: angle.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const validationStatus = getProjectAngleValidationStatus(angle);
  // Angles are identified by role ("Main view") and position ("View 2"…) for
  // accessibility, never by raw upload filenames. Mirrors Apple/Android.
  const displayName = angle.attributes.is_main
    ? t('conceptAngles.position.1')
    : t('angleNumbered', { number: index + 1 });

  const isPending = isProjectAngleValidating(angle);
  const isError =
    validationStatus === 'invalid' ||
    validationStatus === 'prohibited' ||
    validationStatus === 'technical_error' ||
    validationStatus === 'different_building';

  return (
    <BriefTile
      buttonRef={setNodeRef}
      style={style}
      dndProps={{ ...attributes, ...listeners }}
      onClick={onEdit}
      ariaLabel={displayName}
      caption={tCaption('caption.view')}
      isDeleting={isDeleting}
      className={isDragging ? 'shadow-lg z-50 opacity-90 touch-none' : 'touch-none'}
    >
      {angle.attributes.file_url ? (
        <>
          <img
            src={angle.attributes.file_url}
            alt={displayName}
            className="w-full h-full object-cover"
          />
          {/* Photo frame overlay: rounded outline visible only at the corners.
              Geometry matches Apple and Android (inset 6, radius 6, arm 14). */}
          <PhotoFrameCorners />
        </>
      ) : angle.attributes.has_source_image === false ? (
        // Constructed view — never photographed, so "No image" would read as a
        // failed upload. Show the camera it actually is, named by its side.
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-muted-foreground">
          <DraftingCompass className="h-12 w-12" />
          {angle.attributes.viewpoint_label && (
            <span className="text-card-meta line-clamp-2 text-center">
              {angle.attributes.viewpoint_label}
            </span>
          )}
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          <span className="text-card-meta">No image</span>
        </div>
      )}

      {/* Pending overlay */}
      {isPending && (
        <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
          <LoadingSpinner size="sm" />
        </div>
      )}

      {/* Error overlay — the message itself lives in the edit dialog now. */}
      {isError && (
        <div
          className={
            validationStatus === 'prohibited'
              ? 'absolute inset-0 flex items-center justify-center bg-destructive/40'
              : 'absolute inset-0 flex items-center justify-center bg-warning/40'
          }
        >
          {validationStatus === 'prohibited' ? (
            <XCircle className="w-6 h-6 text-destructive" />
          ) : (
            <AlertTriangle className="w-6 h-6 text-warning" />
          )}
        </div>
      )}

      {/* Deleting overlay */}
      {isDeleting && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/40">
          <LoadingSpinner size="sm" />
        </div>
      )}
    </BriefTile>
  );
}
